import React from 'react';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Dumbbell,
  Minus,
  MoreVertical,
} from '../../../bravosIcons';
import { formatLibraryDate } from '../../../../../lib/dateUtils';
import LibraryEmptyState from '../components/LibraryEmptyState';
import { LIB_WORKOUTS_PAGE_SIZE, type LibraryWorkoutsApi } from '../../hooks/useLibraryWorkouts';
import { getWorkoutExerciseCount, isTrainerOwnedLibraryRow } from '../../utils/workoutLibraryRowUtils';

export type WorkoutsTableDesktopProps = {
  wk: LibraryWorkoutsApi;
  workouts: any[];
  profileId: string | undefined;
  selectedWorkouts: Set<string>;
  openWorkoutMenu: string | null;
  setOpenWorkoutMenu: (id: string | null) => void;
  workoutMenuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  trainerWorkoutDuplicateInFlight: boolean;
  libPageSelectionState: 'none' | 'some' | 'all';
  onToggleSelectPage: () => void;
  onSelectWorkout: (workoutId: string) => void;
  onWorkoutClick: (workout: any, e: React.MouseEvent) => void;
  onEditWorkout: (workout: any) => void;
  onDuplicateWorkout: (workout: any) => void;
  onDeleteWorkout: (workoutId: string, workoutName: string) => void;
  onCreateWorkout: () => void;
  onAssignWorkout: (workoutId: string) => void;
};

/** Desktop Library — Workouts tab table, empty states, pagination (§6 / §9). */
export function WorkoutsTableDesktop({
  wk,
  workouts,
  profileId,
  selectedWorkouts,
  openWorkoutMenu,
  setOpenWorkoutMenu,
  workoutMenuRefs,
  trainerWorkoutDuplicateInFlight,
  libPageSelectionState,
  onToggleSelectPage,
  onSelectWorkout,
  onWorkoutClick,
  onEditWorkout,
  onDuplicateWorkout,
  onDeleteWorkout,
  onCreateWorkout,
  onAssignWorkout,
}: WorkoutsTableDesktopProps) {
  if (wk.libWorkoutsEmptyVariant !== null) {
    return (
      <LibraryEmptyState
        variant={wk.libWorkoutsEmptyVariant}
        tab="workouts"
        searchQuery={wk.workoutNameFilter}
        hasActiveFilters={wk.libWorkoutsHasActiveControls}
        onClearAll={wk.handleClearAllWorkouts}
        onCreate={onCreateWorkout}
      />
    );
  }

  return (
    <div className="lib-table-wrap">
      <table className="lib-table">
        <thead>
          <tr>
            <th className="lib-col-select" aria-label="Select column">
              <button
                type="button"
                className={`lib-checkbox ${libPageSelectionState === 'all' ? 'is-checked' : libPageSelectionState === 'some' ? 'is-indeterminate' : ''}`}
                onClick={onToggleSelectPage}
                aria-label="Select all workouts on this page"
                aria-checked={libPageSelectionState === 'all' ? true : libPageSelectionState === 'some' ? 'mixed' : false}
                role="checkbox"
              >
                {libPageSelectionState === 'all' && <Check />}
                {libPageSelectionState === 'some' && <Minus />}
              </button>
            </th>
            <th className="lib-col-index">№</th>
            <th
              className={`is-sortable ${wk.workoutSortColumn === 'name' ? 'is-sorted' : ''}`}
              onClick={(e) => wk.handleWorkoutSort('name', e)}
              aria-sort={wk.workoutSortColumn === 'name' ? (wk.workoutSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              <span className="lib-th-inner">
                Name
                {wk.workoutSortColumn === 'name' ? (
                  wk.workoutSortDirection === 'asc' ? <ChevronUp /> : <ChevronDown />
                ) : (
                  <ChevronDown style={{ opacity: 0.4 }} />
                )}
              </span>
            </th>
            <th>Muscle group</th>
            <th>Equipment</th>
            <th
              className={`is-sortable ${wk.workoutSortColumn === 'createdAt' ? 'is-sorted' : ''}`}
              onClick={(e) => wk.handleWorkoutSort('createdAt', e)}
              aria-sort={wk.workoutSortColumn === 'createdAt' ? (wk.workoutSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              <span className="lib-th-inner">
                Created
                {wk.workoutSortColumn === 'createdAt' ? (
                  wk.workoutSortDirection === 'asc' ? <ChevronUp /> : <ChevronDown />
                ) : (
                  <ChevronDown style={{ opacity: 0.4 }} />
                )}
              </span>
            </th>
            <th className="lib-col-actions" aria-label="Row actions" />
          </tr>
        </thead>
        <tbody>
          {wk.filteredAndSortedWorkouts.length === 0 ? (
            <tr>
              <td colSpan={7} className="lib-empty">
                <Dumbbell aria-hidden="true" />
                <h3>
                  {workouts.length === 0 ? 'No workouts yet' : 'No workouts match your filters'}
                </h3>
                <p>
                  {workouts.length === 0
                    ? 'Get started by creating your first workout.'
                    : 'Try adjusting search, chips, or sort.'}
                </p>
                {workouts.length === 0 && (
                  <button type="button" className="lib-empty-cta" onClick={onCreateWorkout}>
                    Create workout
                  </button>
                )}
              </td>
            </tr>
          ) : (
            wk.libWorkoutsPageRows.map((workout, indexOnPage) => {
              const globalIndex = (wk.libWorkoutsPage - 1) * LIB_WORKOUTS_PAGE_SIZE + indexOnPage;
              const exerciseCount = getWorkoutExerciseCount(workout);
              const isSelected = selectedWorkouts.has(workout.id);
              const isWorkoutMenuOpen = openWorkoutMenu === workout.id;
              const focusTags: string[] = Array.isArray(workout.focusTags) ? workout.focusTags : [];
              const equipmentTags: string[] = Array.isArray(workout.equipmentTags) ? workout.equipmentTags : [];
              const workoutIsYou = isTrainerOwnedLibraryRow(workout.createdBy, profileId);

              return (
                <tr
                  key={`workout-${workout.id}`}
                  className={`${isSelected ? 'is-selected' : ''}${isWorkoutMenuOpen ? ' lib-row-menu-open' : ''}`}
                  onClick={(e) => {
                    if (openWorkoutMenu !== null) return;
                    const target = e.target as HTMLElement;
                    if (
                      target.closest('.workout-menu-item')
                      || target.closest('.workout-menu')
                      || target.closest('.lib-checkbox')
                      || target.closest('.lib-kebab')
                      || target.closest('.lib-kebab-menu')
                    ) {
                      return;
                    }
                    onWorkoutClick(workout, e);
                  }}
                >
                  <td className="lib-col-select" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`lib-checkbox ${isSelected ? 'is-checked' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWorkout(workout.id);
                      }}
                      aria-label={`Select row ${workout.name}`}
                      aria-checked={isSelected}
                      role="checkbox"
                    >
                      {isSelected && <Check />}
                    </button>
                  </td>
                  <td className="lib-col-index">
                    <span className="lib-row-index">{String(globalIndex + 1).padStart(2, '0')}</span>
                  </td>
                  <td>
                    <div className="lib-row-name">{workout.name}</div>
                    <div className="lib-row-subtitle">
                      <span className="lib-tnum">{exerciseCount}</span>{' '}
                      {exerciseCount === 1 ? 'exercise' : 'exercises'}
                    </div>
                  </td>
                  <td>
                    <div className="lib-tag-row">
                      {focusTags.length === 0 ? (
                        <span className="lib-muscle">Full body</span>
                      ) : (
                        <>
                          {focusTags.slice(0, 3).map((t) => (
                            <span key={`focus-${workout.id}-${t}`} className="lib-muscle">
                              {t}
                            </span>
                          ))}
                          {focusTags.length > 3 && (
                            <span className="lib-muscle lib-tag-more" title={focusTags.slice(3).join(', ')}>
                              +{focusTags.length - 3}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="lib-tag-row">
                      {equipmentTags.length === 0 ? (
                        <span className="lib-tag">Bodyweight</span>
                      ) : (
                        <>
                          {equipmentTags.slice(0, 3).map((t) => (
                            <span key={`equip-${workout.id}-${t}`} className="lib-tag">
                              {t}
                            </span>
                          ))}
                          {equipmentTags.length > 3 && (
                            <span className="lib-tag lib-tag-more" title={equipmentTags.slice(3).join(', ')}>
                              +{equipmentTags.length - 3}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="lib-row-date">{formatLibraryDate(workout.createdAt)}</span>
                  </td>
                  <td className="lib-col-actions workout-menu" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-block"
                      ref={(el) => {
                        workoutMenuRefs.current[workout.id] = el;
                      }}
                    >
                      <button
                        type="button"
                        className={`lib-kebab${isWorkoutMenuOpen ? ' is-open' : ''}`}
                        aria-haspopup="menu"
                        aria-expanded={isWorkoutMenuOpen}
                        aria-label="Workout actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenWorkoutMenu(isWorkoutMenuOpen ? null : workout.id);
                        }}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {isWorkoutMenuOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-[90]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenWorkoutMenu(null);
                            }}
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{ touchAction: 'none' }}
                          />
                          <div
                            className="lib-kebab-menu workout-menu-item"
                            onClick={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            role="menu"
                          >
                            <button
                              type="button"
                              className="lib-menu-item workout-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenWorkoutMenu(null);
                                onEditWorkout(workout);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={trainerWorkoutDuplicateInFlight}
                              className="lib-menu-item workout-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (trainerWorkoutDuplicateInFlight) return;
                                setOpenWorkoutMenu(null);
                                void onDuplicateWorkout(workout);
                              }}
                            >
                              Duplicate
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="lib-menu-item workout-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenWorkoutMenu(null);
                                onAssignWorkout(workout.id);
                              }}
                            >
                              Assign to a member
                            </button>
                            {workoutIsYou && (
                              <>
                                <div className="lib-kebab-menu-divider" role="separator" />
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="lib-menu-item lib-menu-item--danger workout-menu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenWorkoutMenu(null);
                                    onDeleteWorkout(workout.id, workout.name);
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {wk.filteredAndSortedWorkouts.length > LIB_WORKOUTS_PAGE_SIZE && (
        <div className="lib-table-foot">
          <div className="lib-foot-meta">
            Showing{' '}
            <strong className="lib-tnum">
              {wk.libRangeStart}–{wk.libRangeEnd}
            </strong>{' '}
            of <strong className="lib-tnum">{wk.filteredAndSortedWorkouts.length}</strong>
          </div>
          <div className="lib-pager" role="navigation" aria-label="Pagination">
            <button
              type="button"
              className="lib-page-btn"
              onClick={() => wk.setLibWorkoutsPage((p) => Math.max(1, p - 1))}
              disabled={wk.libWorkoutsPage === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {wk.libBuildPageWindow(wk.libWorkoutsPage, wk.libWorkoutsTotalPages).map((p, i) =>
              p === 'ellipsis' ? (
                <span key={`ellipsis-${i}`} className="lib-page-ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={`page-${p}`}
                  type="button"
                  className={`lib-page-btn ${p === wk.libWorkoutsPage ? 'is-active' : ''}`}
                  onClick={() => wk.setLibWorkoutsPage(p)}
                  aria-label={`Go to page ${p}`}
                  aria-current={p === wk.libWorkoutsPage ? 'page' : undefined}
                >
                  {p}
                </button>
              ),
            )}
            <button
              type="button"
              className="lib-page-btn"
              onClick={() => wk.setLibWorkoutsPage((p) => Math.min(wk.libWorkoutsTotalPages, p + 1))}
              disabled={wk.libWorkoutsPage === wk.libWorkoutsTotalPages}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
